import { useCallback, useEffect, useState } from 'react';
// Imported from the `/urql` subpath, not the package root: the root's `types` field
// points at `dist/index.d.ts`, which is only regenerated by an api-services build, so a
// freshly added operation is invisible there. `/urql` maps straight to the source.
import { proStudioUrqlGql } from '@pypestream/api-services/urql';

import {
  sendProStudioEvent,
  useProStudioCtxSelector,
} from '../xstate/app.xstate';
import type { AgentManagerFileLike } from '../utils';

/**
 * The three file types the Files tabs render. `assets` (Media) is deliberately absent —
 * that tab still reads `ctx.selectedBot.assets` and is out of scope for this migration.
 */
export type AgentManagerListType = 'config' | 'scripts' | 'custom-assets';

export type UseAgentManagerFilesResult = {
  files: AgentManagerFileLike[];
  isLoading: boolean;
  error?: string;
  /** Re-runs the query. Call after an upload or a delete. */
  refetch: () => void;
  /** Params the mutations and REST calls need; undefined until a solution is selected. */
  solutionId?: string;
  versionName?: string;
  organisationName?: string;
  projectId?: string;
};

const QUERY_BY_TYPE = {
  config: (vars: { solutionId: string; versionName: string }) =>
    proStudioUrqlGql
      .getConfigsFiles(vars)
      .then((r) => ({ data: r.data?.admin_?.configsFiles, error: r.error })),
  scripts: (vars: { solutionId: string; versionName: string }) =>
    proStudioUrqlGql
      .getScriptsFiles(vars)
      .then((r) => ({ data: r.data?.admin_?.scriptsFiles, error: r.error })),
  'custom-assets': (vars: { solutionId: string; versionName: string }) =>
    proStudioUrqlGql.getCustomAssetsFiles(vars).then((r) => ({
      data: r.data?.admin_?.customAssetsFiles,
      error: r.error,
    })),
} as const;

/**
 * Loads one type of solution file from the Agent Manager API.
 *
 * Queried directly rather than through an XState invoke because the data is
 * screen-local and needs an imperative refetch after every upload and delete — the same
 * reason the presence and publish-status hooks talk to `proStudioUrqlGql` directly.
 */
export const useAgentManagerFiles = (
  type: AgentManagerListType
): UseAgentManagerFilesResult => {
  const { solutionId, versionName, organisationName, projectId } =
    useProStudioCtxSelector((ctx) => ({
      solutionId: ctx.selectedBot?.uniqueId,
      versionName: ctx.draftVersion ?? undefined,
      organisationName: ctx.accountId,
      projectId: ctx.project?.id,
    }));

  const [files, setFiles] = useState<AgentManagerFileLike[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [nonce, setNonce] = useState(0);

  const refetch = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    // Every query needs both, and there is nothing to show without a draft version.
    if (!solutionId || !versionName) {
      setFiles([]);
      setError(undefined);
      // Also clears a load that was in flight when the solution changed: its `finally`
      // skips this because the effect cleanup marked it cancelled, and without it the
      // sections would show a permanent spinner instead of their empty state.
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(undefined);

    // urql's OperationResultSource is only PromiseLike, so it has no .catch/.finally —
    // hence the explicit try/finally rather than a promise chain.
    const run = async () => {
      try {
        const { data, error: queryError } = await QUERY_BY_TYPE[type]({
          solutionId,
          versionName,
        });
        if (cancelled) return;
        if (queryError) {
          setError(queryError.message);
          setFiles([]);
          return;
        }
        const loaded = (data ?? []).filter(Boolean) as AgentManagerFileLike[];
        setFiles(loaded);
        // The validator reads the draft's scripts from context, not from this hook, so
        // an upload or delete here has to reach it too (PSTUDIO-3336).
        if (type === 'scripts') {
          sendProStudioEvent({
            type: 'setDraftScriptFiles',
            versionName,
            files: loaded
              .map((file) => file.path)
              .filter((path): path is string => Boolean(path)),
          });
        }
      } catch (caught) {
        if (cancelled) return;
        setError(
          caught instanceof Error ? caught.message : 'Failed to load files'
        );
        setFiles([]);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [type, solutionId, versionName, nonce]);

  return {
    files,
    isLoading,
    error,
    refetch,
    solutionId,
    versionName,
    organisationName,
    projectId,
  };
};
